import React from 'react';
import styled from '@emotion/styled';
import { breakpoints, spacing } from '@naf/theme';
import { Card } from '@naf-simba/sections-cards';
import { Tag } from '@naf-simba/elements-tag';
import { useNavigate, useLocation } from 'react-router-dom';
import { AdvancedImage, lazyload } from '@cloudinary/react';
import { getLabelVariant } from '@lib/getLabelVariant';
import { isNotTransientProp } from '../../lib/shouldForwardProp';
import { useCloudinary } from '../../hooks/Cloudinary/useCloudinary';
import useSelector from '../../redux/typedHooks';
import { BenefitsType } from '../../../../types/benefitsType';
import { ApplicationState } from '../../redux/modules/application';
import { MembershipBenefitReference } from '../../../../types/membershipBenefitReferenceType';
import MobileAppIllustration from '../../../assets/images/phone-image.svg?url';
import defaultBenefitImage from '../../../assets/images/defaultLocalBenefit.svg?url';
import { formatDate } from '../../utils/Dates';
import { ScrollList } from '../scroll-list/ScrollList';

interface Props {
  benefitItems: Record<string, BenefitsType> | BenefitsType[] | MembershipBenefitReference[];
  listView?: boolean;
  showAppLink?: boolean;
  size?: 'standard' | 'small';
}

const BenefitItemCard = ({
  benefit: {
    image,
    name,
    title,
    slug,
    isNewBenefit,
    campaignDetails,
    partner,
    id,
    discountBadge,
    isLocalContent,
    localBenefitImage,
  },
  listView,
  app,
}: {
  benefit: BenefitsType | MembershipBenefitReference;
  listView?: boolean;
  app?: ApplicationState;
}) => {
  const itemSlug = slug || id;
  const navigate = useNavigate();
  const location = useLocation();
  const { getProcessedImage } = useCloudinary();

  const cldImage = app && getProcessedImage(image?.publicId, { width: 300 });
  const localImage = localBenefitImage?.url ? localBenefitImage?.url : defaultBenefitImage;
  const handleLocalPath = () => {
    if (location.pathname.includes('/fordeler')) return location.pathname.replace(/\/[^/]*$/, `/${slug}`);
    return `${location.pathname}/fordeler/${slug}`;
  };

  const hasActiveCampaign = campaignDetails?.endDate && new Date(campaignDetails.endDate) > new Date();
  const formattedDate = campaignDetails?.endDate ? formatDate(campaignDetails?.endDate) : null;

  return (
    <StyledCard
      $listView={listView}
      media={
        cldImage
          ? {
              render: () => (
                <AdvancedImage
                  alt={image?.altText || ''}
                  cldImg={cldImage}
                  plugins={[lazyload({ rootMargin: '10px 20px 10px 30px', threshold: 0.25 })]}
                />
              ),
            }
          : {
              src: isLocalContent ? localImage : image?.srcSet?.jpgLarge || '',
              alt: isLocalContent ? localBenefitImage?.caption || '' : image?.altText || '',
            }
      }
      onClick={() => {
        navigate(isLocalContent ? handleLocalPath() : `/medlemskap/medlemsfordeler/${itemSlug}`);
      }}
      title={name || title}
      label={
        isNewBenefit || hasActiveCampaign
          ? {
              text: isNewBenefit ? 'Ny fordel' : `Varer til ${formattedDate}`,
              ...getLabelVariant(isNewBenefit ? 'signature' : 'moss'),
            }
          : undefined
      }
      footer={discountBadge ? <Tag text={`${discountBadge}`} color="alternative" /> : undefined}
      logo={partner?.logo ? <PartnerLogo src={partner.logo.srcSet.jpgLarge} alt="Partnerlogo" /> : undefined}
    />
  );
};

const BenefitsList = ({ benefitItems, listView = false, showAppLink = false, size = 'small' }: Props) => {
  const navigate = useNavigate();
  const { getLogo } = useCloudinary();
  const application = useSelector((state) => state.application);
  const logo = useSelector((state) => state.globalSettings.settings?.logo);
  const benefits = !(benefitItems instanceof Array) ? Object.values(benefitItems) : benefitItems;
  const logoImage = (logo && logo.publicId && getLogo(logo.publicId).toURL()) || undefined;

  // If preview is true, it should not be listed but reachable with url.
  const visibleBenefits = benefits.filter((benefitItem) => !benefitItem.preview);

  const benefitsToShow = visibleBenefits.slice(0, 3);

  return visibleBenefits && !listView ? (
    <ScrollList>
      {benefitsToShow.map((benefitItem) => (
        <BenefitItemCard benefit={benefitItem} app={application} key={`s-${benefitItem.slug}`} />
      ))}
    </ScrollList>
  ) : (
    <BenefitListWrapper $size={size}>
      {showAppLink && (
        <StyledCard
          $listView={listView}
          key="benefitApp"
          media={{
            src: MobileAppIllustration,
            alt: 'Mobiltelefon',
            fit: 'contain',
          }}
          onClick={() => {
            navigate(`/naf-app`);
          }}
          title="Få fordelene i lomma med NAF appen"
          logo={<PartnerLogo src={logoImage} alt="Partnerlogo" />}
        />
      )}
      {benefitsToShow.map((benefitItem) => (
        <BenefitItemCard benefit={benefitItem} app={application} key={benefitItem.slug} listView />
      ))}
    </BenefitListWrapper>
  );
};

const PartnerLogo = styled.img`
  height: 24px;
`;

/**
 * The retired `CardBenefits` sized itself (25% for `small`, 33% for `standard`);
 * the Simba `Card` fills its container, so the row owns the column width now.
 * Tracks rather than flex bases: the cards then share the width exactly, and a
 * row is as tall as its tallest card.
 */
const BenefitListWrapper = styled('div', { shouldForwardProp: isNotTransientProp })<{ $size: 'standard' | 'small' }>`
  display: grid;
  grid-template-columns: repeat(${({ $size }) => ($size === 'small' ? 4 : 3)}, minmax(0, 1fr));
  gap: ${spacing.space32};
  align-items: stretch;

  @media (max-width: ${breakpoints.m}) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const StyledCard = styled(Card, { shouldForwardProp: isNotTransientProp })<{ $listView?: boolean }>`
  &:hover {
    ${PartnerLogo} {
      transform: none;
    }
  }

  @media (max-width: ${breakpoints.m}) {
    margin-bottom: ${(props) => (props.$listView ? spacing.space32 : '0')};
    min-width: ${(props) => (props.$listView ? '100%' : '75%')};
  }
`;

export default BenefitsList;
